import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { eventInfo as mockEventInfo, venue, socialLinks } from '../data/mock';
import Header from '../components/Header';
import Hero from '../components/Hero';
import LogoBridge from '../components/LogoBridge';
import Concept from '../components/Concept';
import Events from '../components/Events';
import Media from '../components/Media';
import Menu from '../components/Menu';
import Venue from '../components/Venue';
import Reservation from '../components/Reservation';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:8000'
    : 'https://crapule-bxl-production.up.railway.app'
);
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [isReservationOpen, setIsReservationOpen] = useState(false);
  const [isHeroInView, setIsHeroInView] = useState(true);
  const [heroLogoElement, setHeroLogoElement] = useState(null);
  const [headerLogoElement, setHeaderLogoElement] = useState(null);
  const [eventInfo, setEventInfo] = useState(mockEventInfo);
  const [pastEvents, setPastEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastMenu, setPastMenu] = useState(null);
  const [pastMenusByEvent, setPastMenusByEvent] = useState([]);
  const [nextMenu, setNextMenu] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);
  const [lastPastEvent, setLastPastEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [featuredMedia, setFeaturedMedia] = useState([]);
  const [lastPastMedia, setLastPastMedia] = useState([]);
  const [nextEventMedia, setNextEventMedia] = useState([]);
  const [lineupsByEventId, setLineupsByEventId] = useState({});
  const [siteMediaEnabled, setSiteMediaEnabled] = useState(true);

  useEffect(() => {
    let active = true;
    let cancelDeferredFetch = () => {};

    const fetchMenuForEvent = async (event, setter) => {
      if (!event) {
        setter(null);
        return;
      }

      try {
        const menuResponse = await axios.get(`${API}/menus/${event.id}`);
        if (active) {
          setter(menuResponse.data.menu || null);
        }
      } catch (menuError) {
        console.error('Error fetching event menu:', menuError);
        if (active) {
          setter(null);
        }
      }
    };

    const fetchMediaForEvent = async (event, setter) => {
      if (!event) {
        setter([]);
        return;
      }

      try {
        const mediaRes = await axios.get(`${API}/media/${event.id}`);
        const items = mediaRes.data.media || [];
        if (active) {
          setter(event.mediaActive ? items : []);
        }
      } catch {
        if (active) {
          setter([]);
        }
      }
    };

    const scheduleDeferredFetch = (callback) => {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        const id = window.requestIdleCallback(callback, { timeout: 1500 });
        return () => window.cancelIdleCallback(id);
      }

      const id = window.setTimeout(callback, 350);
      return () => window.clearTimeout(id);
    };

    const fetchAllData = async () => {
      try {
        // Fetch all events
        const eventsResponse = await axios.get(`${API}/events`);
        if (!active) return;
        const allPastEvents = eventsResponse.data.pastEvents || [];
        const allUpcomingEvents = eventsResponse.data.upcomingEvents || [];
        const upcomingEvent = eventsResponse.data.nextEvent || null;
        const mostRecentPastEvent = allPastEvents[0] || null;
        
        setPastEvents(allPastEvents);
        setUpcomingEvents(allUpcomingEvents);
        setLastPastEvent(mostRecentPastEvent);

        if (upcomingEvent) {
          setNextEvent(upcomingEvent);
          setEventInfo(prev => ({
            ...prev,
            currentVolume: {
              number: upcomingEvent.volume,
              theme: upcomingEvent.theme,
              date: upcomingEvent.date,
              time: upcomingEvent.time,
              price: upcomingEvent.price,
              status: upcomingEvent.status,
              reservationsOpen: upcomingEvent.reservationsOpen,
              timeSlotsEnabled: !!upcomingEvent.timeSlotsEnabled,
              slotDurationMinutes: upcomingEvent.slotDurationMinutes || null
            },
            nextVolume: {
              number: upcomingEvent.volume,
              theme: upcomingEvent.theme,
              date: upcomingEvent.date,
              time: upcomingEvent.time,
              price: upcomingEvent.price,
              status: upcomingEvent.status,
              reservationsOpen: upcomingEvent.reservationsOpen,
              timeSlotsEnabled: !!upcomingEvent.timeSlotsEnabled,
              slotDurationMinutes: upcomingEvent.slotDurationMinutes || null
            }
          }));

        } else {
          const nextVolNumber = allPastEvents.length > 0 ? (allPastEvents[0].volume + 1) : 1;
          setNextEvent(null);
          setNextMenu(null);
          setNextEventMedia([]);
          setEventInfo(prev => ({
            ...prev,
            currentVolume: {
              number: nextVolNumber,
              theme: 'À DÉCOUVRIR',
              date: '',
              time: '',
              price: null,
              status: 'upcoming',
              reservationsOpen: false
            },
            nextVolume: {
              number: nextVolNumber,
              theme: 'À DÉCOUVRIR',
              date: '',
              time: '',
              price: null,
              status: 'upcoming',
              reservationsOpen: false
            }
          }));
        }

        if (!mostRecentPastEvent) {
          setPastMenu(null);
          setLastPastMedia([]);
          setPastMenusByEvent([]);
        }

        if (!active) return;
        setLoading(false);

        cancelDeferredFetch = scheduleDeferredFetch(async () => {
          const uniqueEvents = Array.from(
            new Map([...allPastEvents, ...allUpcomingEvents].map((event) => [event.id, event])).values()
          );

          await Promise.allSettled([
            fetchMenuForEvent(upcomingEvent, setNextMenu),
            fetchMenuForEvent(mostRecentPastEvent, setPastMenu),
            fetchMediaForEvent(upcomingEvent, setNextEventMedia),
            fetchMediaForEvent(mostRecentPastEvent, setLastPastMedia),
            (async () => {
              try {
                const featRes = await axios.get(`${API}/featured-media`);
                const items = (featRes.data.items || []).map(i => i.media);
                if (active) {
                  setFeaturedMedia(items);
                }
              } catch {
                if (active) {
                  setFeaturedMedia([]);
                }
              }
            })(),
            (async () => {
              if (uniqueEvents.length === 0) {
                if (active) {
                  setLineupsByEventId({});
                }
                return;
              }

              try {
                const entries = await Promise.all(uniqueEvents.map(async (ev) => {
                  try {
                    const res = await axios.get(`${API}/lineup/${ev.id}`);
                    return [ev.id, res.data || { visible: false, artists: [] }];
                  } catch {
                    return [ev.id, { visible: false, artists: [] }];
                  }
                }));

                if (active) {
                  setLineupsByEventId(Object.fromEntries(entries));
                }
              } catch {
                if (active) {
                  setLineupsByEventId({});
                }
              }
            })(),
            (async () => {
              if (allPastEvents.length === 0) {
                if (active) {
                  setPastMenusByEvent([]);
                }
                return;
              }

              try {
                const menus = await Promise.all(allPastEvents.map(async (ev) => {
                  try {
                    const res = await axios.get(`${API}/menus/${ev.id}`);
                    if (res.data.menu && res.data.menu.dishes && res.data.menu.dishes.length) {
                      return { event: ev, menu: res.data.menu };
                    }
                    return null;
                  } catch {
                    return null;
                  }
                }));

                if (active) {
                  setPastMenusByEvent(menus.filter(Boolean));
                }
              } catch {
                if (active) {
                  setPastMenusByEvent([]);
                }
              }
            })(),
          ]);

          if (active) {
            setMediaLoading(false);
          }
        });

      } catch (error) {
        console.error('Error fetching data:', error);
        if (active) {
          setMediaLoading(false);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchAllData();

    return () => {
      active = false;
      cancelDeferredFetch();
    };
  }, []);
  
  useEffect(() => {
    let active = true;

    const loadSiteMediaConfig = async () => {
      try {
        const res = await axios.get(`${API}/site-media/public-config`);
        const enabled = res.data?.enabled !== false;
        if (!active) return;
        setSiteMediaEnabled(enabled);
        try {
          localStorage.setItem('siteMediaEnabled', enabled ? 'true' : 'false');
        } catch {}
      } catch {
        try {
          const v = localStorage.getItem('siteMediaEnabled');
          if (active) {
            setSiteMediaEnabled(v === null ? true : v === 'true');
          }
        } catch {}
      }
    };

    loadSiteMediaConfig();

    const onStorage = (e) => {
      if (e.key === 'siteMediaEnabled') {
        try {
          const v = localStorage.getItem('siteMediaEnabled');
          setSiteMediaEnabled(v === null ? true : v === 'true');
        } catch {}
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      active = false;
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const openReserveWithPortal = (e) => {
    try {
      const x = e?.clientX ?? window.innerWidth / 2;
      const y = e?.clientY ?? window.innerHeight / 2;
      const portal = document.createElement('div');
      portal.style.position = 'fixed';
      portal.style.left = `${x}px`;
      portal.style.top = `${y}px`;
      portal.style.width = '20px';
      portal.style.height = '20px';
      portal.style.borderRadius = '50%';
      portal.style.pointerEvents = 'none';
      portal.style.background = 'radial-gradient(circle, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 45%, rgba(0,0,0,0) 75%)';
      portal.style.boxShadow = '0 0 120px 20px rgba(255,255,255,0.06) inset, 0 0 220px rgba(0,0,0,0.7)';
      portal.style.transform = 'translate(-50%, -50%) scale(0)';
      portal.style.opacity = '1';
      portal.style.transition = 'transform 450ms cubic-bezier(.2,.7,0,.95), opacity 600ms ease-out';
      document.body.appendChild(portal);
      requestAnimationFrame(() => {
        portal.style.transform = 'translate(-50%, -50%) scale(120)';
        portal.style.opacity = '0';
      });
      setTimeout(() => setIsReservationOpen(true), 220);
      setTimeout(() => portal.remove(), 800);
    } catch {
      setIsReservationOpen(true);
    }
  };

  return (
    <div className="text-white bg-black" style={{ minHeight: 'var(--app-height)' }}>
      <LogoBridge enabled sourceElement={heroLogoElement} targetElement={headerLogoElement} />
      <Header
        onReserveClick={(e) => openReserveWithPortal(e)}
        siteMediaEnabled={siteMediaEnabled}
        socialLinks={socialLinks}
        hideBrandUntilHeroOut
        isHeroInView={isHeroInView}
        enableBrandBridge
        onBrandTextElementChange={setHeaderLogoElement}
      />
      <Hero
        eventInfo={eventInfo}
        onReserveClick={(e) => openReserveWithPortal(e)}
        onVisibilityChange={setIsHeroInView}
        onActiveLogoElementChange={setHeroLogoElement}
      />
      <Concept eventInfo={eventInfo} />
      <Events
        pastEvents={pastEvents}
        upcomingEvents={upcomingEvents}
        loading={loading}
        lineupsByEventId={lineupsByEventId}
        onReserveClick={(e) => openReserveWithPortal(e)}
      />
      <Menu 
        pastMenu={pastMenu} 
        nextMenu={nextMenu} 
        lastPastEvent={lastPastEvent}
        nextEvent={nextEvent}
        loading={loading}
        showDrinks={false}
        pastMenusByEvent={pastMenusByEvent}
      />
      {siteMediaEnabled && (
        <Media 
          featuredMedia={featuredMedia}
          lastPastEvent={lastPastEvent}
          lastPastMedia={lastPastMedia}
          nextEvent={nextEvent}
          nextEventMedia={nextEventMedia}
          siteMediaEnabled={siteMediaEnabled}
          loading={loading || mediaLoading}
        />
      )}

      <Venue venue={venue} />
      <Reservation 
        isOpen={isReservationOpen} 
        onClose={() => setIsReservationOpen(false)}
        eventInfo={eventInfo}
      />
      <Footer socialLinks={socialLinks} />
    </div>
  );
};

export default Home;
